import { ReactNode } from "react";
import styled from "styled-components";

import {
  CollapsibleContainer,
  CollapsibleContainerBody,
  CollapsibleContainerHeader,
} from "@vfuk/core-collapsible-container";
import Container from "@vfuk/core-container";
import { GridColumn, GridRow } from "@vfuk/core-grid";
import SimpleSkeletonLoader from "@vfuk/core-simple-skeleton-loader";

import classes from "./TableRow.module.scss";

export const RowDataContainer = styled.div<{
  gridTemplateColumns: string | undefined;
}>`
  display: grid;
  grid-auto-flow: column;
  margin-left: 30px;
  box-sizing: border-box;
  word-wrap: break-word;
  word-break: break-word;
  grid-column-gap: 3.1rem;
  grid-template-columns: ${({ gridTemplateColumns }) =>
    gridTemplateColumns ?? "repeat(auto-fit, minmax(7rem, 1fr))"};

  /* If the screen is less than 1280px in width, reduce gap  to save some space */
  @media (max-width: 1280px) {
    grid-column-gap: 0.05rem;
    align-items: center;
    grid-template-columns: ${({ gridTemplateColumns }) =>
      gridTemplateColumns ?? "repeat(auto-fit, minmax(5.6rem, 1fr))"};
  }
`;

export const ContainerHeaderNew = styled.div`
  div:nth-child(1) {
    flex: 1 1 0%;
  }
  button:focus,
  button:focus-visible,
  button:focus-within {
    box-shadow: none;
  }
`;
export const ContainerBody = styled.div`
  background-color: #ebebeb;
  border-radius: 5px;
  padding: 16px 55px;
`;

const CollapsibleContainerDiv = styled.div<{ marginLeft: string }>`
  margin-left: ${({ marginLeft }) => marginLeft};
`;

const StyledButton = styled.button<{ isIconDisabled?: boolean }>`
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
  pointer-events: ${(isIconDisabled) => (isIconDisabled ? "none" : "all")};
  opacity: ${(isIconDisabled) => (isIconDisabled ? 0.4 : 1)};
`;

const StyledCheckboxContainer = styled.div<{ marginBottom: string }>`
  margin-bottom: ${({ marginBottom }) => marginBottom};
`;

type TableRowProps = {
  checkbox?: ReactNode;
  rowData?: Array<ReactNode>;
  canViewIcon?: boolean;
  onIconClick?: () => void;
  icon?: ReactNode;
  isIconDisabled?: boolean;
  noMargin?: boolean;
  noElevation?: boolean;
  gridTemplateColumns?: string;
  notes?: string | JSX.Element;
  accordionData?: ReactNode;
  showCheckboxes?: boolean;
};

type TableRowSkeletonProps = {
  skeleton?: boolean;
  colCount?: number;
  colWidths?: string[];
  checkbox?: ReactNode;
};

export const TableRowAccordion = ({
  checkbox,
  rowData,
  canViewIcon,
  onIconClick,
  icon,
  isIconDisabled,
  colCount,
  skeleton,
  noMargin,
  noElevation,
  gridTemplateColumns,
  notes,
  accordionData,
  showCheckboxes,
}: TableRowProps & TableRowSkeletonProps): JSX.Element => {
  const isSkeleton = () => skeleton && colCount !== undefined;
  return (
    <GridRow marginBottom={noMargin ? 0 : 3} marginTop={noMargin ? 2 : 3}>
      <GridColumn>
        <div className={classes.TableRowContainer} role="row">
          <Container
            elevation={!noElevation}
            paddingLevel={1}
            allowOverflow={true}
            zIndex={0}
          >
            {checkbox && (
              <StyledCheckboxContainer
                marginBottom={showCheckboxes ? "-25px" : ""}
                className={
                  checkbox
                    ? classes.TableRowCheckbox
                    : classes.TableRowNoCheckbox
                }
              >
                {checkbox}
              </StyledCheckboxContainer>
            )}

            <>
              <CollapsibleContainerDiv
                marginLeft={showCheckboxes ? "25px" : ""}
              >
                <CollapsibleContainer>
                  <ContainerHeaderNew>
                    <CollapsibleContainerHeader>
                      {isSkeleton() ? (
                        <div>
                          <RowDataContainer
                            gridTemplateColumns={gridTemplateColumns}
                            data-testid="loading-skeleton"
                          >
                            {[...Array(colCount)].map((skeleton, i) => (
                              <SimpleSkeletonLoader
                                key={i}
                                type="rect"
                                width={{
                                  sm: skeleton?.colWidths
                                    ? skeleton.colWidths[i] || "75%"
                                    : "75%",
                                }}
                                height={{ sm: "25px" }}
                              />
                            ))}
                          </RowDataContainer>
                        </div>
                      ) : (
                        <div>
                          <RowDataContainer
                            gridTemplateColumns={gridTemplateColumns}
                          >
                            {rowData?.map((value, i) => (
                              <div
                                className={classes.RowDataCell}
                                role="cell"
                                key={`row${i}`}
                              >
                                {value}
                              </div>
                            ))}
                          </RowDataContainer>
                          <div className={classes.Icon}>
                            {canViewIcon && (
                              <StyledButton
                                isIconDisabled={isIconDisabled}
                                disabled={isIconDisabled}
                                onClick={() => onIconClick && onIconClick()}
                                role="button"
                              >
                                {icon}
                              </StyledButton>
                            )}
                          </div>
                        </div>
                      )}
                      {notes && <>{notes}</>}
                    </CollapsibleContainerHeader>
                  </ContainerHeaderNew>
                  <CollapsibleContainerBody>
                    <ContainerBody>{accordionData}</ContainerBody>
                  </CollapsibleContainerBody>
                </CollapsibleContainer>
              </CollapsibleContainerDiv>
            </>
          </Container>
        </div>
      </GridColumn>
    </GridRow>
  );
};
